'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import useEmblaCarousel, { type UseEmblaCarouselType } from 'embla-carousel-react';
import { urlFor } from '@/sanity/lib/image';

export interface ProductImage {
  _key: string;
  alt?: string;
  asset: {
    _ref?: string;
    url: string;
  };
  hotspot?: unknown;
  crop?: unknown;
}

interface UseProductDetailCarouselResult {
  emblaRef: (node: HTMLElement | null) => void;
  emblaApi: UseEmblaCarouselType[1];
  validImages: ProductImage[];
  currentIndex: number;
  isFullscreen: boolean;
  setIsFullscreen: (open: boolean) => void;
  handlePrevious: () => void;
  handleNext: () => void;
  handleThumbnailClick: (index: number) => void;
  isImageLoaded: (index: number) => boolean;
  suppressTransition: boolean;
}

export const useProductDetailCarousel = (images: ProductImage[]): UseProductDetailCarouselResult => {
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set([0]));
  const [suppressTransition, setSuppressTransition] = useState<boolean>(false);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: images?.length > 1,
    align: 'start',
    containScroll: 'trimSnaps',
    duration: 20,
  });

  const validImages = useMemo(() => {
    return (images || []).filter((img) => {
      const hasValidAsset = img.asset?._ref && img.asset._ref !== 'image-' && img.asset._ref.length > 10;
      const hasUrl = img.asset?.url;
      return Boolean(hasValidAsset || hasUrl);
    });
  }, [images]);

  const firstImageKey = useMemo(() => validImages[0]?._key ?? '', [validImages]);

  const preloadImage = useCallback((imageUrl: string, index: number) => {
    setLoadedImages((prev) => {
      if (prev.has(index)) return prev;
      const next = new Set(prev);
      // Optimistically mark as loading to avoid race on rapid swipes
      next.add(index);
      return next;
    });

    const img = new Image();
    img.onload = () => {
      setLoadedImages((prev) => new Set(prev).add(index));
    };
    img.src = imageUrl;
  }, []);

  const handlePrevious = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollPrev();
    } else {
      setCurrentIndex((prev) => (validImages.length === 0 ? 0 : prev === 0 ? validImages.length - 1 : prev - 1));
    }
  }, [emblaApi, validImages.length]);

  const handleNext = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollNext();
    } else {
      setCurrentIndex((prev) => (validImages.length === 0 ? 0 : prev === validImages.length - 1 ? 0 : prev + 1));
    }
  }, [emblaApi, validImages.length]);

  const handleThumbnailClick = useCallback(
    (index: number) => {
      if (emblaApi) emblaApi.scrollTo(index);
      else setCurrentIndex(index);
    },
    [emblaApi],
  );

  // Sync index with Embla and preload neighbors
  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => {
      const selected = emblaApi.selectedScrollSnap();
      setCurrentIndex(selected);

      const prevIndex = selected === 0 ? validImages.length - 1 : selected - 1;
      const nextIndex = selected === validImages.length - 1 ? 0 : selected + 1;
      const prevImage = validImages[prevIndex];
      const nextImage = validImages[nextIndex];
      if (prevImage) {
        const url =
          prevImage.asset?.url ||
          (prevImage.asset?._ref && prevImage.asset._ref !== 'image-'
            ? urlFor(prevImage)?.width(800).quality(90).url()
            : null);
        if (url) preloadImage(url, prevIndex);
      }
      if (nextImage) {
        const url =
          nextImage.asset?.url ||
          (nextImage.asset?._ref && nextImage.asset._ref !== 'image-'
            ? urlFor(nextImage)?.width(800).quality(90).url()
            : null);
        if (url) preloadImage(url, nextIndex);
      }
    };

    emblaApi.on('select', onSelect);
    onSelect();
  }, [emblaApi, validImages, preloadImage]);

  // Preload all images except first
  useEffect(() => {
    validImages.forEach((image, index) => {
      if (index !== 0) {
        const imageUrl =
          image.asset?.url ||
          (image.asset?._ref && image.asset._ref !== 'image-' ? urlFor(image)?.width(800).quality(90).url() : null);
        if (imageUrl) preloadImage(imageUrl, index);
      }
    });
  }, [validImages, preloadImage]);

  // Reset selection on images change
  useEffect(() => {
    // Suppress fade/transition during variant switch to avoid glitch
    setSuppressTransition(true);
    setCurrentIndex(0);
    setLoadedImages(new Set([0]));
    if (emblaApi) emblaApi.scrollTo(0, false);
    // Release suppression on next frame (twice to ensure styles applied)
    const id = requestAnimationFrame(() => {
      const id2 = requestAnimationFrame(() => setSuppressTransition(false));
      return () => cancelAnimationFrame(id2);
    });
    return () => cancelAnimationFrame(id);
  }, [firstImageKey, validImages.length, emblaApi]);

  // Guard current index when images change
  useEffect(() => {
    if (currentIndex >= validImages.length && validImages.length > 0) {
      setCurrentIndex(0);
      if (emblaApi) emblaApi.scrollTo(0, false);
    }
  }, [currentIndex, validImages.length, emblaApi]);

  // Re-init Embla only when slide count changes
  useEffect(() => {
    if (emblaApi) emblaApi.reInit();
  }, [emblaApi, validImages.length]);

  const isImageLoaded = useCallback(
    (index: number) => loadedImages.has(index) || index === currentIndex,
    [loadedImages, currentIndex],
  );

  return {
    emblaRef,
    emblaApi,
    validImages,
    currentIndex,
    isFullscreen,
    setIsFullscreen,
    handlePrevious,
    handleNext,
    handleThumbnailClick,
    isImageLoaded,
    suppressTransition,
  };
};
